import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Activity, 
  Clock, 
  UserCheck,
  Menu, 
  X,
  Settings,
  ChevronDown,
  Upload,
  Download,
  Info,
  LogOut,
  Phone,
  Mail,
  Building,
  User,
  Briefcase,
  RefreshCw,
  PlayCircle,
  MoreHorizontal,
  ClipboardList
} from 'lucide-react';

const Header = ({ 
  currentUser,
  activeTab,
  setActiveTab,
  rollOffCandidates = [],
  candidates = [],
  isMenuOpen,
  setIsMenuOpen,
  showProfileDropdown,
  setShowProfileDropdown,
  showExportDropdown,
  setShowExportDropdown,
  setShowActivityLogModal,
  setShowUserManagementModal,
  setShowImportInstructions,
  setShowAdvancedExportModal,
  handleFileSelect,
  downloadTemplate,
  isLoading,
  success,
  API_BASE_URL,
  setShowSyncModal,
  isSyncing,
  setHasPendingSheetUpdates,
  setShowTutorialModal  
}) => {
  
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  
  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 z-40 shadow-sm">
      <div className="w-full mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between w-full py-3">
          
          {/* Logo Section - Enhanced */}
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur"></div>
              <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center overflow-hidden ring-2 ring-slate-200 group-hover:ring-blue-400 transition-all duration-300 transform group-hover:scale-105">
                <img
                  src="images.png" 
                  alt="Company Logo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text whitespace-nowrap">
                CMP
              </h1>
              <p className="text-xs text-slate-500 font-medium">
                {currentUser ? (
                  <span className="flex items-center space-x-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                    <span>{currentUser.role} Portal</span>
                  </span>
                ) : (
                  'Loading...'
                )}
              </p>
            </div>
          </div>

          {/* Desktop Navigation - Enhanced with Modern Design */}
          <nav className="hidden lg:flex items-center justify-center space-x-1 xl:space-x-2 flex-1 max-w-4xl mx-4">
            {[
  ...(currentUser?.role !== 'Account Manager' ? [{ id: 'dashboard', label: 'Dashboard', icon: Activity, gradient: 'from-blue-600 to-purple-600' }] : []),
  { id: 'candidates', label: 'Candidates', icon: Users, gradient: 'from-blue-600 to-cyan-600' },
              { 
                id: 'rolloff', 
                label: 'Roll-off', 
                icon: Clock, 
                gradient: 'from-red-600 to-pink-600',
                badge: rollOffCandidates.length 
              }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative group px-3 xl:px-4 py-2 rounded-xl font-semibold text-xs xl:text-sm transition-all duration-300 transform hover:scale-105 ${
                    isActive
                      ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg shadow-${tab.gradient.split('-')[1]}-500/30`
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  {/* Glow Effect for Active Tab */}
                  {isActive && (
                    <div className={`absolute inset-0 bg-gradient-to-r ${tab.gradient} rounded-xl opacity-50 blur-xl transition-opacity duration-300`}></div>
                  )}
                  
                  <div className="relative flex items-center space-x-1.5">
                    <Icon className={`w-4 h-4 ${isActive ? 'animate-pulse' : ''}`} />
                    <span className="whitespace-nowrap">{tab.label}</span>
                    
                    {/* Enhanced Badge */}
                    {tab.badge !== undefined && tab.badge > 0 && (
                      <div className="relative">
                        <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold rounded-full ${
                          isActive 
                            ? 'bg-white text-red-600' 
                            : 'bg-red-500 text-white'
                        } shadow-lg animate-pulse`}>
                          {tab.badge}
                        </span>
                        <span className="absolute inset-0 bg-red-400 rounded-full animate-ping opacity-75"></span>
                      </div>
                    )}
                  </div>
                  
                  {/* Active Indicator Line */}
                  {isActive && (
                    <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-1 bg-gradient-to-r ${tab.gradient} rounded-t-full`}></div>
                  )}
                </button>
              );
            })}
            
            {/* More Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowMoreDropdown(!showMoreDropdown)}
                className={`relative group px-3 xl:px-4 py-2 rounded-xl font-semibold text-xs xl:text-sm transition-all duration-300 transform hover:scale-105 ${
                  ['interview', 'interviewed', 'rd', 'survey'].includes(activeTab)
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/30'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                {['interview', 'interviewed', 'rd', 'survey'].includes(activeTab) && (
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl opacity-50 blur-xl transition-opacity duration-300"></div>
                )}
                
                <div className="relative flex items-center space-x-1.5">
                  <MoreHorizontal className={`w-4 h-4 ${['interview', 'interviewed', 'rd', 'survey'].includes(activeTab) ? 'animate-pulse' : ''}`} />
                  <span className="whitespace-nowrap">More</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${showMoreDropdown ? 'rotate-180' : ''}`} />
                  
                </div>
                
                {['interview', 'interviewed', 'rd', 'survey'].includes(activeTab) && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-1 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-full"></div>
                )}
              </button>
              
              {/* More Dropdown Menu */}
              {showMoreDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-[45]"
                    onClick={() => setShowMoreDropdown(false)}
                  ></div>
                  
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-slate-200 z-[46] overflow-hidden">
                    <div className="py-2">
                      {currentUser?.role !== 'Account Manager' && <button
                        onClick={() => {
                          setActiveTab('interview');
                          setShowMoreDropdown(false);
                        }}
                        className={`flex items-center w-full px-4 py-3 text-sm transition-colors group ${
                          activeTab === 'interview'
                            ? 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-10 h-10 bg-gradient-to-br from-emerald-100 to-teal-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform ${
                          activeTab === 'interview' ? 'ring-2 ring-emerald-300' : ''
                        }`}>
                          <Briefcase className="w-5 h-5 text-emerald-600" />
                        </div>
                       <span className="font-semibold">Interview Panel</span>
                      </button>}
                      
                      <button
                        onClick={() => {
                          setActiveTab('interviewed');
                          setShowMoreDropdown(false);
                        }}
                        className={`flex items-center w-full px-4 py-3 text-sm transition-colors group ${
                          activeTab === 'interviewed'
                            ? 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform ${
                          activeTab === 'interviewed' ? 'ring-2 ring-amber-300' : ''
                        }`}>
                          <UserCheck className="w-5 h-5 text-amber-600" />
                        </div>
                        <span className="font-semibold">Interviewed</span>
                      </button>
                      
                      {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
                        <button
                          onClick={() => {
                            setActiveTab('rd');
                            setShowMoreDropdown(false);
                          }}
                          className={`flex items-center w-full px-4 py-3 text-sm transition-colors group ${
                            activeTab === 'rd'
                              ? 'bg-gradient-to-r from-purple-50 to-indigo-50 text-purple-700'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <div className={`w-10 h-10 bg-gradient-to-br from-purple-100 to-indigo-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform ${
                            activeTab === 'rd' ? 'ring-2 ring-purple-300' : ''
                          }`}>
                            <UserCheck className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="flex-1 flex items-center justify-between">
                            <span className="font-semibold">RD Team</span>
                          </div>
                        </button>
                      )}
                      
                      {currentUser?.role !== 'Account Manager' && <button
                        onClick={() => {
                          setActiveTab('survey');
                          setShowMoreDropdown(false);
                        }}
                        className={`flex items-center w-full px-4 py-3 text-sm transition-colors group ${
                          activeTab === 'survey'
                            ? 'bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-10 h-10 bg-gradient-to-br from-blue-100 to-cyan-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform ${
                          activeTab === 'survey' ? 'ring-2 ring-blue-300' : ''
                        }`}>
                          <ClipboardList className="w-5 h-5 text-blue-600" />
                        </div>
                        <span className="font-semibold">Survey</span>
                      </button>}
                    </div>
                  </div>
                </>
              )}
            </div>
          </nav>

          {/* Desktop Actions - Enhanced */}
          <div className="hidden lg:flex items-center space-x-2 xl:space-x-3 justify-end flex-shrink-0">
            
            {/* Google Sheets Sync Button */}
            {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
              <button 
                onClick={() => setShowSyncModal(true)}
                disabled={isSyncing}
                className="group flex items-center px-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all text-xs xl:text-sm font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 transition-transform duration-300 ${isSyncing ? 'animate-spin' : 'group-hover:rotate-180'}`} />
                <span className="hidden xl:inline whitespace-nowrap">
                  {isSyncing ? 'Syncing...' : 'Sync with GSheet'}
                </span>
                <span className="xl:hidden">Sync</span>
              </button>
            )}
            
            {/* Data Tools Dropdown */}
            {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead' || currentUser?.role === 'BU Head' || currentUser?.role === 'Account Manager') && (
  <div className="relative export-dropdown-container">
                <button 
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  className="group flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all text-xs xl:text-sm font-medium shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:scale-105"
                >
                  <Settings className="w-4 h-4 mr-1.5 group-hover:rotate-90 transition-transform duration-300" />
                  <span className="hidden xl:inline whitespace-nowrap">Data Tools</span>
                  <span className="xl:hidden">Tools</span>
                  <ChevronDown className={`w-4 h-4 ml-1.5 transition-transform duration-300 ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Enhanced Dropdown Menu */}
                {showExportDropdown && (
                  <>
                    {/* Backdrop for click outside on mobile */}
                    <div 
                      className="fixed inset-0 z-[45] lg:hidden"
                      onClick={() => setShowExportDropdown(false)}
                    ></div>
                    
                    <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 z-[46] overflow-hidden max-h-[calc(100vh-100px)] overflow-y-auto">
                      <div className="py-2">
  {/* Import Section */}
  {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
  <><div className="px-4 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-50">
    Import Data
  </div>
                        
                        {[
                          { icon: Upload, label: 'Import CSV/Excel', sublabel: 'Upload candidate data', color: 'purple', isFile: true },
                          { icon: Download, label: 'Download Template', sublabel: 'Get CSV template', color: 'indigo', action: () => { downloadTemplate(); setShowExportDropdown(false); } },
                          { icon: Info, label: 'Import Help', sublabel: 'Instructions & guide', color: 'blue', action: () => { setShowImportInstructions(true); setShowExportDropdown(false); } }
                        ].map((item, idx) => {
                          const Icon = item.icon;
                          return item.isFile ? (
                            <div key={idx} className="relative group">
                              <input
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileSelect}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                              />
                              <button className="flex items-center w-full px-4 py-3.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors group-hover:bg-gradient-to-r group-hover:from-purple-50 group-hover:to-pink-50">
                                <div className="w-11 h-11 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                                  <Icon className="w-5 h-5 text-purple-600" />
                                </div>
                                <div className="text-left flex-1">
                                  <div className="font-semibold text-slate-900">{isLoading ? 'Importing...' : item.label}</div>
                                  <div className="text-xs text-slate-500 mt-0.5">{item.sublabel}</div>
                                </div>
                              </button>
                            </div>
                          ) : (
                            <button
                              key={idx}
                              onClick={item.action}
                              className="flex items-center w-full px-4 py-3.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors group hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50"
                            >
                              <div className="w-11 h-11 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                                <Icon className="w-5 h-5 text-blue-600" />
                              </div>
                              <div className="text-left flex-1">
                                <div className="font-semibold text-slate-900">{item.label}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{item.sublabel}</div>
                              </div>
                            </button>
                          );
                        })}
                        
                        </>)}
{/* Export Section */}
<div className="px-4 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-50 mt-2 border-t border-slate-200">
  Export Data
</div>

<button
  onClick={async () => {
    const role = currentUser?.role;
const userId = currentUser?.id;
const fullName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim();
const userEmail = currentUser?.email || '';
let exportUrl = `${API_BASE_URL}/export_rolloff.php`;
if (role === 'BU Head' || role === 'Account Manager') {
  exportUrl += `?user_id=${userId}&role=${encodeURIComponent(role)}&name=${encodeURIComponent(fullName)}&email=${encodeURIComponent(userEmail)}`;
}
    setShowExportDropdown(false);

    window.open(exportUrl, '_blank');
  }}
  className="flex items-center w-full px-4 py-3.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors group hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50"
>
  <div className="w-11 h-11 bg-gradient-to-br from-red-100 to-pink-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
    <Clock className="w-5 h-5 text-red-600" />
  </div>
  <div className="text-left flex-1">
    <div className="font-semibold text-slate-900">Roll Off Report</div>
    <div className="text-xs text-slate-500 mt-0.5">Summary + raw data (.xlsx)</div>
  </div>
</button>

{(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && [{icon: Download, label: 'Quick CSV Export', sublabel: 'Export all candidates', color: 'green', action: () => { window.open(`${API_BASE_URL}/export.php?format=csv`, '_blank'); setShowExportDropdown(false); } },
  { icon: Settings, label: 'Advanced Export', sublabel: 'Custom fields & formats', color: 'orange', action: () => { setShowAdvancedExportModal(true); setShowExportDropdown(false); } }
].map((item, idx) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={idx}
                              onClick={item.action}
                              className="flex items-center w-full px-4 py-3.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors group hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50"
                            >
                              <div className="w-11 h-11 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                                <Icon className="w-5 h-5 text-green-600" />
                              </div>
                              <div className="text-left flex-1">
                                <div className="font-semibold text-slate-900">{item.label}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{item.sublabel}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Enhanced Profile Dropdown */}
            <div className="relative profile-dropdown" data-profile-trigger>
              <button 
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="group flex items-center space-x-2 p-1.5 pr-3 text-slate-700 hover:bg-slate-100 rounded-xl transition-all duration-300 border-2 border-transparent hover:border-slate-200"
                data-profile-trigger
              >
                <div className="relative">
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center ring-2 ring-slate-200 group-hover:ring-blue-400 transition-all duration-300 transform group-hover:scale-110">
                    <span className="text-xs font-bold text-white">
                      {currentUser ? 
                        `${currentUser.first_name?.charAt(0) || ''}${currentUser.last_name?.charAt(0) || ''}` : 
                        'U'
                      }
                    </span>
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></div>
                </div>
                <div className="text-left hidden xl:block">
                  <p className="text-xs font-semibold text-slate-900 truncate max-w-[100px]">
                    {currentUser ? 
                      `${currentUser.first_name || ''} ${currentUser.last_name || ''}` : 
                      'User'
                    }
                  </p>
                  <p className="text-xs text-slate-500 font-medium">
                    {currentUser ? currentUser.role : 'Loading...'}
                  </p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 transition-colors" />
              </button>
              
              {/* Enhanced Dropdown Menu */}
              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 z-[46] overflow-hidden profile-dropdown max-h-[calc(100vh-80px)] overflow-y-auto">
                  {/* Profile Header */}
                  <div className="p-4 bg-gradient-to-br from-slate-50 to-blue-50 border-b border-slate-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                        <span className="text-lg font-bold text-white">
                          {currentUser ? 
                            `${currentUser.first_name?.charAt(0) || ''}${currentUser.last_name?.charAt(0) || ''}` : 
                            'U'
                          }
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {currentUser ? 
                            `${currentUser.first_name || ''} ${currentUser.last_name || ''}` : 
                            'User'
                          }
                        </p>
                        <p className="text-xs text-slate-600 truncate">
                          {currentUser ? currentUser.email : 'email@example.com'}
                        </p>
                        <span className="inline-flex items-center px-2 py-0.5 mt-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          {currentUser ? currentUser.role : 'User'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Menu Items */}
                  <div className="py-2">

                    {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
                      <button 
                        onClick={() => {
                          setShowActivityLogModal(true);
                          setShowProfileDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-slate-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-colors group"
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                          <Activity className="w-5 h-5 text-blue-600" />
                        </div>
                        <span className="font-medium">Activity Log</span>
                      </button>
                    )}
                    
                    <button 
                      onClick={() => {
                        setShowUserManagementModal(true);
                        setShowProfileDropdown(false);
                      }}
                      className={`flex items-center w-full px-4 py-3 text-sm transition-colors group ${
                        currentUser?.role !== 'Admin' ? 'opacity-50 cursor-not-allowed' : 'text-slate-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50'
                      }`}
                      disabled={currentUser?.role !== 'Admin'}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                        <Users className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 text-left">
                        <span className="font-medium">User Management</span>
                        {currentUser?.role !== 'Admin' && (
                          <span className="block text-xs text-red-500 mt-0.5">Admin Only</span>
                        )}
                      </div>
                    </button>
                    
                    <div className="border-t border-slate-200 my-2"></div>
                    
                    {/* Watch Tutorial Button */}
                    <button 
                      onClick={() => {
                        setShowTutorialModal(true);
                        setShowProfileDropdown(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-slate-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-red-50 transition-colors group"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-red-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                        <PlayCircle className="w-5 h-5 text-orange-600" />
                      </div>
                      <span className="font-medium">Watch Tutorial</span>
                    </button>
                    
                    <div className="border-t border-slate-200 my-2"></div>
                    
                    {/* Sign Out Button */}
                    <button 
                      type="button"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        try {
                          setShowProfileDropdown(false);
                          localStorage.clear();
                          sessionStorage.clear();
                          
                          success('Signing out...');
                          
                          fetch('https://test.vdpl.co/Candidates/Candidates/logout.php', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include'
                          }).catch(() => {});
                          
                          if (window.handleAppLogout) {
                            window.handleAppLogout();
                          } else {
                            setTimeout(() => {
                              window.location.href = '/';
                            }, 500);
                          }
                        } catch (err) {
                          localStorage.clear();
                          sessionStorage.clear();
                          if (window.handleAppLogout) {
                            window.handleAppLogout();
                          } else {
                            window.location.href = '/';
                          }
                        }
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors group cursor-pointer"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                        <LogOut className="w-5 h-5 text-red-600" />
                      </div>
                      <span className="font-medium">Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile Menu Toggle - Enhanced */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all duration-300 border-2 border-transparent hover:border-slate-200"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Enhanced Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white shadow-xl">
          <div className="px-4 py-4 space-y-2 max-h-[calc(100vh-80px)] overflow-y-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Activity, gradient: 'from-blue-600 to-purple-600' },
              { id: 'candidates', label: 'Candidates', icon: Users, gradient: 'from-blue-600 to-cyan-600' },
              { 
                id: 'rolloff', 
                label: 'Roll-off', 
                icon: Clock, 
                gradient: 'from-red-600 to-pink-600',
                badge: rollOffCandidates.length 
              },
              ...(currentUser?.role !== 'Account Manager' ? [{ 
                id: 'interview', 
                label: 'Interview Panel', 
                icon: Briefcase, 
                gradient: 'from-emerald-600 to-teal-600'
              },
              { 
                id: 'interviewed', 
                label: 'Interviewed Candidates', 
                icon: UserCheck, 
                gradient: 'from-amber-600 to-orange-600'
              }] : []),
              ...(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead' ? [{
                id: 'rd',
                label: 'RD Team',
                icon: UserCheck,
                gradient: 'from-purple-600 to-indigo-600'
              }] : []),
              ...(currentUser?.role !== 'Account Manager' ? [{ 
                id: 'survey', 
                label: 'Survey', 
                icon: ClipboardList, 
                gradient: 'from-blue-600 to-cyan-600'
              }] : [])
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => {setActiveTab(tab.id); setIsMenuOpen(false);}}
                  className={`relative w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 ${
                    isActive
                      ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg`
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 border-2 border-slate-200'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'animate-pulse' : ''}`} />
                  <span className="flex-1 text-left">{tab.label}</span>
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                      isActive ? 'bg-white text-red-600' : 'bg-red-500 text-white'
                    } shadow-lg animate-pulse`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
            
            <div className="pt-4 border-t border-slate-200 space-y-2">
              {/* Watch Tutorial in Mobile Menu */}
              <button 
                onClick={() => {
                  setShowTutorialModal(true);
                  setIsMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 transition-all shadow-lg"
              >
                <PlayCircle className="w-5 h-5" />
                <span>Watch Tutorial</span>
              </button>

              {(currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead') && (
                <button 
                  onClick={() => {
                    setShowActivityLogModal(true);
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors border-2 border-slate-200"
                >
                  <Activity className="w-5 h-5" />
                  <span>Activity Log</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;